import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { StyledTableCell, StyledTableRow } from "../../utils/styled";
import LoginService from "../../services/users.service";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const rolLabel = {
  personal: "Personal",
  write: "Write",
  admin: "Admin",
};

function ListUsers() {
  const [users, setUsers] = useState([]);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [snackbarInfo, setSnackbarInfo] = useState({
    text: "",
    type: "",
    open: false,
  });

  const currentUser = JSON.parse(localStorage.getItem("currentUser"));
  const isAdmin = currentUser?.rol === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    const unsubscribe = LoginService.getAll((list) => setUsers(list));
    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [isAdmin]);

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setUserToDelete(null);
  };

  const handleDeleteUser = () => {
    if (!userToDelete) return;
    LoginService.remove(userToDelete.userName)
      .then(() => {
        setSnackbarInfo({
          text: "Usuario eliminado correctamente",
          type: "success",
          open: true,
        });
        handleCloseDeleteModal();
      })
      .catch((err) => {
        setSnackbarInfo({
          text: "Error al eliminar el usuario",
          type: "error",
          open: true,
        });
        handleCloseDeleteModal();
      });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarInfo((prev) => ({ ...prev, open: false }));
  };

  const isCurrentUser = (user) =>
    currentUser && currentUser.userName === user.userName;

  if (!isAdmin) {
    return (
      <Container component="main" maxWidth="xl">
        <Typography color="error">No tienes permiso para ver esta página.</Typography>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xl">
      <Typography variant="h5">Listado de usuarios</Typography>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <Button variant="contained" href="/register">
          Nuevo usuario
        </Button>
      </div>
      <TableContainer>
        <Table aria-label="tabla de usuarios">
          <TableHead>
            <TableRow>
              <StyledTableCell>Usuario</StyledTableCell>
              <StyledTableCell>Email</StyledTableCell>
              <StyledTableCell>Rol</StyledTableCell>
              <StyledTableCell>Acciones</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user, index) => (
              <StyledTableRow key={user.userName || index}>
                <StyledTableCell>{user.userName || "-"}</StyledTableCell>
                <StyledTableCell>{user.email || "-"}</StyledTableCell>
                <StyledTableCell>
                  {rolLabel[user.rol] || user.rol || "-"}
                </StyledTableCell>
                <StyledTableCell className="column-actions">
                  <IconButton
                    aria-label="eliminar"
                    disabled={isCurrentUser(user)}
                    onClick={() => {
                      setUserToDelete(user);
                      setOpenDeleteModal(true);
                    }}
                    title={
                      isCurrentUser(user)
                        ? "No puedes eliminar tu propio usuario"
                        : "Eliminar usuario"
                    }
                  >
                    <DeleteIcon sx={{ color: "red" }} />
                  </IconButton>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          ¿Desea eliminar este usuario?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {userToDelete && (
              <>
                Se eliminará el usuario <strong>{userToDelete.userName}</strong> (
                {userToDelete.email}). Los cambios serán permanentes.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal}>Cerrar</Button>
          <Button onClick={handleDeleteUser} color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarInfo.open}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarInfo.type}
          sx={{ width: "100%" }}
        >
          {snackbarInfo.text}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default ListUsers;
